// Correcciones en useSocket.js
// Esta es la parte crítica que debe modificarse

import { useEffect, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useClientAuth } from '../contexts/ClientAuthContext';

export const useSocket = () => {
    const { client, clientToken } = useClientAuth();
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const socketRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const maxReconnectAttempts = 5;

    // Crear una nueva conexión socket
    const createSocketConnection = useCallback(() => {
        // Validar que tenemos la autenticación necesaria
        if (!clientToken || !client || !client.id) {
            console.warn('No se puede crear socket: falta token o datos de cliente', {
                hasToken: !!clientToken,
                hasClient: !!client,
                clientId: client?.id
            });
            setConnectionError('No hay sesión de cliente válida');
            return null;
        }

        console.log('Creando nueva conexión de socket con credenciales:', {
            tokenLength: clientToken?.length,
            clientId: client.id
        });

        try {
            // CAMBIO IMPORTANTE: Asegurarse que el token no tenga prefijo "Bearer"
            const tokenToUse = clientToken.startsWith('Bearer ')
                ? clientToken.slice(7)  // Quitar "Bearer " si existe
                : clientToken;

            // Crear nueva instancia de socket con mejores opciones
            const socketInstance = io(process.env.REACT_APP_SOCKET_URL || '', {
                auth: {
                    // CAMBIO CRÍTICO: Estructura de autenticación simplificada
                    token: tokenToUse,
                    clientId: client.id,
                    type: 'client' // Añadir tipo explícito
                },
                // Usar polling primero para asegurar la conexión inicial, luego intentar websocket
                transports: ['polling', 'websocket'],
                reconnection: true,
                reconnectionAttempts: 3,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                timeout: 10000
            });

            // Configurar manejadores de eventos
            socketInstance.on('connect', () => {
                console.log('Socket conectado con ID:', socketInstance.id);
                setIsConnected(true);
                setConnectionError(null);
                reconnectAttemptsRef.current = 0; // Resetear contador al conectar con éxito

                // Enviar evento de ping para verificar la conexión
                try {
                    socketInstance.emit('ping', { timestamp: Date.now() });
                } catch (pingError) {
                    console.warn('Error al enviar ping inicial:', pingError);
                }
            });

            socketInstance.on('connect_error', (error) => {
                console.error('Error al conectar socket:', error.message);
                setConnectionError(`Error de conexión: ${error.message}`);
                setIsConnected(false);

                // Programar reconexión manual si falla la automática
                if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                    reconnectAttemptsRef.current++;

                    // Usar backoff exponencial
                    const delay = Math.min(1000 * Math.pow(1.5, reconnectAttemptsRef.current), 10000);

                    console.log(`Reintentando conexión en ${delay / 1000}s (intento ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

                    if (reconnectTimerRef.current) {
                        clearTimeout(reconnectTimerRef.current);
                    }

                    reconnectTimerRef.current = setTimeout(() => {
                        console.log('Ejecutando reconexión programada...');
                        reconnect();
                    }, delay);
                } else {
                    console.error(`Máximo de reintentos alcanzado (${maxReconnectAttempts})`);
                }
            });

            socketInstance.on('disconnect', (reason) => {
                console.log('Socket desconectado:', reason);
                setIsConnected(false);

                // Solo programar reconexión para ciertos tipos de desconexión
                if (reason === 'io server disconnect' || reason === 'transport close') {
                    setConnectionError('La conexión se ha cerrado. Reconectando...');
                    if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                        reconnect();
                    }
                }
            });

            socketInstance.on('reconnect', (attempt) => {
                console.log(`Socket reconectado después de ${attempt} intentos`);
                setConnectionError(null);
            });

            socketInstance.on('reconnect_attempt', (attempt) => {
                console.log(`Intento de reconexión #${attempt}`);

                // En cada intento, actualizar los datos de autenticación
                socketInstance.auth = {
                    token: tokenToUse,
                    clientId: client.id,
                    type: 'client'
                };
            });

            socketInstance.on('error', (error) => {
                console.error('Error en socket:', error);
                setConnectionError(`Error: ${typeof error === 'string' ? error : 'Error de comunicación'}`);
            });

            // Recibir respuesta de ping del servidor
            socketInstance.on('pong', (data) => {
                console.log('Respuesta de pong recibida:', data);
                // La conexión está funcionando correctamente
            });

            return socketInstance;
        } catch (error) {
            console.error('Error al crear instancia de socket:', error);
            setConnectionError(`Error al inicializar: ${error.message}`);
            return null;
        }
    }, [clientToken, client]);

    // Función para forzar una reconexión
    const reconnect = useCallback(() => {
        console.log('Forzando reconexión de socket...');

        // Limpiar conexión actual
        if (socketRef.current) {
            try {
                socketRef.current.disconnect();
            } catch (error) {
                console.warn('Error al desconectar socket:', error);
            }
            socketRef.current = null;
            setSocket(null);
        }

        // Crear nueva conexión
        const newSocket = createSocketConnection();
        if (newSocket) {
            socketRef.current = newSocket;
            setSocket(newSocket);
        }
    }, [createSocketConnection]);

    // Efecto para crear y mantener la conexión socket
    useEffect(() => {
        // Si no tenemos credenciales, no hacer nada
        if (!clientToken || !client) {
            if (socketRef.current) {
                console.log('Cerrando conexión de socket por falta de autenticación');
                socketRef.current.disconnect();
                socketRef.current = null;
                setSocket(null);
                setIsConnected(false);
            }
            return;
        }

        // Si ya hay un socket conectado, no crear uno nuevo
        if (socketRef.current?.connected) {
            console.log('Socket ya conectado, actualizando credenciales si es necesario');
            // Actualizar credenciales
            const tokenToUse = clientToken.startsWith('Bearer ')
                ? clientToken.slice(7)
                : clientToken;

            socketRef.current.auth = {
                token: tokenToUse,
                clientId: client.id,
                type: 'client'
            };
            return;
        }

        // Crear nueva conexión si no existe
        if (!socketRef.current) {
            console.log('No hay socket, creando nueva conexión...');
            const newSocket = createSocketConnection();
            if (newSocket) {
                socketRef.current = newSocket;
                setSocket(newSocket);
            }
        }

        // Limpiar al desmontar
        return () => {
            console.log('Limpiando conexión de socket (desmontaje)');
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }

            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
                setSocket(null);
            }
        };
    }, [clientToken, client, createSocketConnection]);

    // Retornar el socket y utilidades
    return {
        socket,
        isConnected,
        error: connectionError,
        reconnect
    };
};

export default useSocket;